import { Content } from '@backstage/core-components';
import { catalogApiRef, useEntity, EntityRefLink } from '@backstage/plugin-catalog-react';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Box,
    makeStyles,
    CardHeader,
    CardActions,
    Button,
} from '@material-ui/core';
import { applicationPageTranslationRef } from '../../translation/application-page/application-page';
import { projectPageTranslationRef } from '../../translation/project-page/project-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useEffect, useState } from 'react';

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        transition: '0.2s',
        display: 'flex',
        flexDirection: 'column',

        '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
        },
    },

    header: {
        color: theme.getPageTheme({ themeId: 'application' }).fontColor,
        padding: theme.spacing(2, 2, 3),
        backgroundImage: theme.getPageTheme({ themeId: 'application' }).backgroundImage,
        backgroundPosition: 0,
        backgroundSize: 'inherit',
    },

    content: {
        flexGrow: 1,
    },

    actions: {
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },

    description: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        minHeight: 50,
    },
}));

export const ProjectApplications = () => {
    const classes = useStyles();
    const { entity } = useEntity();
    const catalogApi = useApi(catalogApiRef);

    const config = useApi(configApiRef);
    const SYS_INFO_BACKEND_URL = 'app.baseUrl';
    const frontendUrl = config.getString(SYS_INFO_BACKEND_URL);

    const tApp = useTranslationRef(applicationPageTranslationRef);
    const tProj = useTranslationRef(projectPageTranslationRef);

    // Obtener aplicaciones del proyecto actual
    const appRefs: any = entity.spec?.applications ?? [];

    // Nuevo: función para obtener aplicaciones de hijos si el proyecto es padre
    const getAllApplications = async () => {
        let allAppRefs = [...appRefs];

        // Si el proyecto es padre (no tiene parent)
        if (!entity.spec?.parent) {
            // Buscar hijos en el catálogo
            const childrenResp = await catalogApi.getEntities({
                filter: [
                    { kind: entity.kind, 'spec.parent': `${entity.kind}:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}` }
                ]
            });
            // Agregar aplicaciones de los hijos
            for (const child of childrenResp.items) {
                if (child.spec?.applications && Array.isArray(child.spec.applications)) {
                    allAppRefs.push(...child.spec.applications);
                }
            }
        }

        // Eliminar duplicados
        allAppRefs = Array.from(new Set(allAppRefs));

        if (!allAppRefs.length) return [];
        const response = await catalogApi.getEntitiesByRefs({ entityRefs: allAppRefs });
        return response.items.filter(Boolean);
    };

    const useCanCreateAppFromProjects = (entity: any) => {
        const identityApi = useApi(identityApiRef);
        const catalogApi = useApi(catalogApiRef);
        const config = useApi(configApiRef);
        const [allowed, setAllowed] = useState(false);

        useEffect(() => {
            let cancelled = false;

            const checkJpPermission = async () => {
                if (!entity) return;

                try {
                    // Check if entity has a project number
                    if (!entity.spec?.number) {
                        console.warn('⚠️ Project missing spec.number - cannot check JP permission');
                        if (!cancelled) {
                            setAllowed(false);
                        }
                        return;
                    }

                    // Get user identity
                    const identity = await identityApi.getBackstageIdentity();
                    const userEntityRef = identity.userEntityRef;
                    const userName = userEntityRef.split('/')[1];

                    // Get user email from catalog
                    let userEmail = userName; // Fallback to username
                    try {
                        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
                        const profile = userEntity?.spec?.profile as any;
                        if (profile?.email) {
                            userEmail = profile.email;
                        }
                    } catch (err) {
                        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
                    }

                    // Check for impersonated user (for development/testing)
                    const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
                    const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
                    const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
                    
                    // If impersonated, try to get email for that user too
                    let effectiveUser = impersonatedUser || userEmail;
                    if (impersonatedUser && impersonatedUser !== userEmail) {
                        try {
                            const impersonatedUserRef = `user:default/${impersonatedUser}`;
                            const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
                            const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
                            if (impersonatedProfile?.email) {
                                effectiveUser = impersonatedProfile.email;
                            }
                        } catch (err) {
                            console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
                        }
                    }

                    // Build project resource identifier
                    const projectNumber = String(entity.spec.number).toUpperCase();
                    const resource = `project:${projectNumber}`;

                    console.log('🔐 Checking JP relation for user:', {
                        user: `user:${effectiveUser}`,
                        relation: 'jp',
                        resource,
                    });

                    // Call backend API to check OpenFGA permission
                    const backendUrl = config.getString('backend.baseUrl');
                    const token = (await identityApi.getCredentials()).token;

                    const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            user: `user:${effectiveUser}`,
                            action: 'can_manage_users',
                            resource: resource,
                        }),
                    });

                    if (!response.ok) {
                        throw new Error(`Permission check failed: ${response.statusText}`);
                    }

                    const data = await response.json();
                    const isJP = data.allowed ?? false;

                    console.log('🔐 JP permission result:', isJP ? '✅ Allowed' : '❌ Denied');

                    if (!cancelled) {
                        setAllowed(isJP);
                    }
                } catch (error) {
                    console.error('🔐 Error checking JP permission:', error);
                    if (!cancelled) {
                        setAllowed(false);
                    }
                }
            };

            checkJpPermission();
            return () => {
                cancelled = true;
            };
        }, [entity, identityApi, catalogApi, config]);

        return allowed;
    }

    const canCreateApp = useCanCreateAppFromProjects(entity);

    // Usar useAsync con la nueva función
    const { loading, error, value: applications } = useAsync(getAllApplications, [entity]);

    if (loading) {
        return (
            <Content>
                <Box display="flex" justifyContent="center"><CircularProgress /></Box>
            </Content>
        );
    }

    if (error) {
        return (
            <Content>
                <Typography color="error">Error al cargar aplicaciones</Typography>
            </Content>
        );
    }

    const handleClickButton = async () => {
        const url = `${frontendUrl}/create/templates/default/create-application?formData=${encodeURIComponent(
            JSON.stringify({
                project: {
                    project: entity.spec?.number || '',
                    projectRef: `project:${entity.metadata.namespace}/${entity.metadata.name}`,
                },
            }),
        )}`;
        window.open(url, '_self');
    };

    return (
        <>
            <Grid container direction='row' alignItems='center' justifyContent='space-between'>
                <Grid item>
                    <Typography variant="h5" gutterBottom>
                        {tProj.t('projectApplications.title')}
                    </Typography>
                </Grid>
                <Grid item>
                    {canCreateApp && (
                        <Button variant="contained" color="primary" onClick={handleClickButton}>
                            {tProj.t('projectApplications.createApplication')}
                        </Button>
                    )}
                </Grid>
            </Grid>

            {(applications && applications?.length > 0) ? (
                <Grid container spacing={3}>
                    {applications?.map((app: any) => (
                        <Grid item xs={12} sm={6} md={4} xl={3} key={app.metadata.uid}>
                            <Card className={classes.card}>
                                <CardHeader
                                    className={classes.header}
                                    title={
                                        <Box>
                                            {app.spec?.category && (
                                                <Typography variant="subtitle2" component="h3">
                                                    {app.spec?.category}
                                                </Typography>
                                            )}
                                            {app.metadata.title && (
                                                <Typography variant="h6" component="h4">
                                                    {app.metadata.title}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <CardContent>

                                    {/* Número */}
                                    <Typography variant="body2" color="textSecondary">
                                        {tApp.t('applicationTableColumns.apmNumber')}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {app.spec?.number ?? '—'}
                                    </Typography>

                                    {/* División */}
                                    <Typography variant="body2" color="textSecondary">{tApp.t('applicationTableColumns.division')}</Typography>
                                    {app.spec?.division ? <EntityRefLink
                                        entityRef={app.spec?.division as string}
                                        defaultKind={'Division'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {app.spec?.division as string || '—'}
                                    </Typography>}

                                    {/* Ciclo de Vida */}
                                    <Typography variant="body2" color="textSecondary">
                                        {tApp.t('applicationTableColumns.lifecycle')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {app.spec?.lifecycle ?? '—'}
                                    </Typography>

                                    {/* Manager */}
                                    <Typography variant="body2" color="textSecondary">{tApp.t('applicationTableColumns.manager')}</Typography>
                                    {app.spec?.manager ? <EntityRefLink
                                        entityRef={app.spec?.manager as string}
                                        defaultKind={'User'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {app.spec?.manager as string || '—'}
                                    </Typography>}

                                    {/* IT Manager */}
                                    <Typography variant="body2" color="textSecondary">{tApp.t('applicationTableColumns.itManager')}</Typography>
                                    {app.spec?.itManager ? <EntityRefLink
                                        entityRef={app.spec?.itManager as string}
                                        defaultKind={'User'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {app.spec?.itManager as string || '—'}
                                    </Typography>}

                                </CardContent>

                                <CardActions className={classes.actions}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        component="a"
                                        href={`/catalog/${app.metadata.namespace ?? 'default'}/${app.kind.toLowerCase()}/${app.metadata.name}`}
                                    >
                                        {tProj.t('projectApplications.moreDetails')}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>) :
                <Typography>{tProj.t('projectApplications.emptyApplications')}</Typography>}
        </>
    );
};
